import {Attendee} from 'Event/attendee'
import {BOLD, CAPS, ITALIC} from 'lib/ui/typography/FontStyleInput'
import {
  CalendarStyles,
  Widget,
  WidgetCalendarProviders,
} from 'Event/Widget/template'
import {
  OBVIO_WIDGET_GOOGLE_URL,
  OBVIO_WIDGET_APPLE_URL,
  OBVIO_WIDGET_OUTLOOK_URL,
  OBVIO_WIDGET_YAHOO_URL,
} from 'Event/Widget/script/config'
import {logError} from 'Event/Widget/script/helpers/errors'
import {loadGoogleFont} from 'Event/Widget/script/helpers/fonts'
import {
  handleAppleClick,
  handleGoogleCalendarClick,
  handleOutlookClick,
  handleYahooClick,
} from 'Event/Widget/script/helpers/url'

type ProviderData = {
  backgroundColor: string
  textColor: string
  hoverBackgroundColor: string
  hoverTextColor: string
  title: string
  icon: string
}

function getProviderData(provider: WidgetCalendarProviders): ProviderData {
  if (provider === 'google') {
    return {
      backgroundColor: '#4285F4',
      textColor: '#FFFFFF',
      hoverBackgroundColor: '#3367D6',
      hoverTextColor: '#FFFFFF',
      title: 'Google',
      icon: OBVIO_WIDGET_GOOGLE_URL,
    }
  }

  if (provider === 'outlook') {
    return {
      backgroundColor: '#0072C6',
      textColor: '#FFFFFF',
      hoverBackgroundColor: '#005A9E',
      hoverTextColor: '#FFFFFF',
      title: 'Outlook',
      icon: OBVIO_WIDGET_OUTLOOK_URL,
    }
  }

  if (provider === 'yahoo') {
    return {
      backgroundColor: '#400191',
      textColor: '#FFFFFF',
      hoverBackgroundColor: '#29006E',
      hoverTextColor: '#FFFFFF',
      title: 'Yahoo',
      icon: OBVIO_WIDGET_YAHOO_URL,
    }
  }

  if (provider === 'apple') {
    return {
      backgroundColor: '#000000',
      textColor: '#FFFFFF',
      hoverBackgroundColor: '#333333',
      hoverTextColor: '#FFFFFF',
      title: 'Apple',
      icon: OBVIO_WIDGET_APPLE_URL,
    }
  }

  logError(`Unknown provider: ${provider}`)

  return {
    backgroundColor: '#000000',
    textColor: '#FFFFFF',
    hoverBackgroundColor: '#333333',
    hoverTextColor: '#FFFFFF',
    title: 'Unknown',
    icon: '',
  }
}

export function renderCalendar(
  container: HTMLElement,
  widget: Widget,
  loginUrl: Attendee['login_url'],
) {
  const calendarStyles = widget.template.calendar.styles

  loadGoogleFont(
    `https://fonts.googleapis.com/css2?family=${font(
      calendarStyles,
    )}&display=swap`,
  )

  //reset container
  container.innerHTML = ''

  const holder = document.createElement('div')
  holder.style.display = 'flex'
  holder.style.flexDirection = 'column'
  holder.style.alignItems = 'center'
  holder.style.justifyContent = 'center'
  holder.style.width = '100%'
  holder.style.gap = '15px'
  container.appendChild(holder)

  const title = document.createElement('div')
  title.style.fontSize = '18px'
  title.style.fontWeight = '500'
  title.style.fontFamily = font(calendarStyles)
  title.innerText = widget.template.calendar.label || ''
  holder.appendChild(title)

  const buttonsHolder = document.createElement('div')
  buttonsHolder.style.display = 'flex'
  buttonsHolder.style.flexDirection = 'row'
  buttonsHolder.style.gap = '10px'
  holder.appendChild(buttonsHolder)

  renderCalendarButton(buttonsHolder, widget, loginUrl, 'google')
  renderCalendarButton(buttonsHolder, widget, loginUrl, 'outlook')
  renderCalendarButton(buttonsHolder, widget, loginUrl, 'yahoo')
  renderCalendarButton(buttonsHolder, widget, loginUrl, 'apple')
}

function renderCalendarButton(
  container: HTMLElement,
  widget: Widget,
  loginUrl: Attendee['login_url'],
  provider: WidgetCalendarProviders,
) {
  if (!widget.template.calendar.providers.includes(provider)) {
    return
  }

  const calendarStyles = widget.template.calendar.styles
  const button = document.createElement('button')

  const {
    backgroundColor,
    textColor,
    hoverBackgroundColor,
    hoverTextColor,
    title,
    icon,
  } = getProviderData(provider)

  // Configurable styles
  button.style.background = backgroundColor
  button.style.color = textColor

  button.style.border = border(calendarStyles)
  button.style.borderRadius = calendarStyles?.borderRadius + 'px' || '0px'
  button.style.fontSize = calendarStyles?.fontSize + 'px' || ''
  button.style.minHeight = calendarStyles?.height + 'px' || 'auto'
  button.style.width = calendarStyles?.width + 'px' || 'auto'
  button.style.fontWeight = calendarStyles.font?.styles?.includes(BOLD)
    ? 'bold'
    : 'normal'
  button.style.fontStyle = calendarStyles.font?.styles?.includes(ITALIC)
    ? 'italic'
    : 'normal'
  button.style.textTransform = calendarStyles.font?.styles?.includes(CAPS)
    ? 'uppercase'
    : 'none'
  button.style.fontFamily = font(calendarStyles)

  // Default styles
  button.style.cursor = 'pointer'
  button.style.display = 'flex'
  button.style.flexDirection = 'row'
  button.style.gap = '8px'
  button.style.alignItems = 'center'
  button.style.justifyContent = 'center'
  button.style.backgroundPosition = 'center'
  button.style.backgroundSize = 'cover'
  button.style.position = 'relative'
  button.style.height = 'auto'
  button.style.padding = '5px'
  button.style.lineHeight = '1.15'
  button.style.minWidth = 'min-content'

  button.style.transition = 'opacity 0.2s ease-in-out'

  button.addEventListener('mouseenter', () => {
    button.style.background = hoverBackgroundColor
    button.style.color = hoverTextColor
    button.style.borderColor =
      calendarStyles?.hoverBorderColor || button.style.borderColor
  })

  button.addEventListener('mouseleave', () => {
    button.style.background = backgroundColor
    button.style.color = textColor
    button.style.borderColor =
      calendarStyles?.borderColor || button.style.borderColor
  })

  // image
  const imageHolder = document.createElement('div')
  imageHolder.style.display = 'flex'
  imageHolder.style.flexDirection = 'row'
  button.appendChild(imageHolder)

  const image = document.createElement('img')
  image.style.display = 'flex'
  image.style.flexDirection = 'row'
  image.style.width = calendarStyles.fontSize
    ? calendarStyles.fontSize + 4 + 'px'
    : '20px'
  image.style.height = calendarStyles.fontSize
    ? calendarStyles.fontSize + 4 + 'px'
    : '20px'
  image.src = icon
  imageHolder.appendChild(image)

  // text
  const text = document.createElement('div')
  text.style.display = 'flex'
  text.style.flexDirection = 'row'
  text.innerHTML = title
  button.appendChild(text)

  container.appendChild(button)

  button.onclick = () => {
    handleClick(widget, loginUrl, provider)
  }
}

function handleClick(
  widget: Widget,
  loginUrl: Attendee['login_url'],
  provider: WidgetCalendarProviders,
) {
  if (provider === 'google') {
    handleGoogleCalendarClick(widget, loginUrl)
  }

  if (provider === 'outlook') {
    handleOutlookClick(widget, loginUrl)
  }

  if (provider === 'yahoo') {
    handleYahooClick(widget, loginUrl)
  }

  if (provider === 'apple') {
    handleAppleClick(widget, loginUrl)
  }
}

function border(buttonStyles: CalendarStyles) {
  if (!buttonStyles.borderWidth) {
    return 'none'
  }

  return `${buttonStyles.borderWidth}px solid ${
    buttonStyles.borderColor || '#000000'
  }`
}

function font(buttonStyles: CalendarStyles) {
  if (!buttonStyles?.font?.family) {
    return 'Rubik'
  }

  if (buttonStyles.font.family === 'inherit') {
    return 'Rubik'
  }

  return buttonStyles.font.family
}
